import { Box, CircularProgress, Theme } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { AppOptimizelyProvider } from '../../config/AppOptimizelyProvider';
import { MemberLoginType } from '../../common/models/Member';
import { useAppDispatch } from '../../store/hooks';
import { useIsLargeScreen } from '../../common/hooks/useResponsive';
import { generateTemporaryLoginCodeSso } from '../../api/loginAPI';
import LoginDesktopLayout from './LoginDesktopLayout';
import { loginMember } from '../../store/thunks/authenticationThunk';
import { Login } from './Login';
import { addSessionId } from '../../store/slices/onboardingSlice';
import { Logger } from '../../config/logging/logger';
import { useCompanyEnrollmentInfoForLogin } from '../../common/useCompanyEnrollmentInfoForLogin';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { normalizeLoginRedirectPath } from '../../common/utils/loginUtils';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { getDashboardEntryRoute } from '../../common/dashboardEntryRoute';
import { MAGIC_LINK_LOGIN_SOURCE, toAutoLoginSource } from '../../common/utils/autoLoginRoute';

export interface OnboardingLayoutProps {
  afterLoginPath?: string
}
export default function LoginLayout({
  afterLoginPath,
}: OnboardingLayoutProps) {
  const isDesktop = useIsLargeScreen();
  const initializedQueryLogin = useRef(false);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const urlAccessToken = searchParams.get('accessToken');
  const urlAfterLoginPath = searchParams.get('afterLoginPath');
  const urlLoginSource = searchParams.get('loginSource');
  const { solutionOrgName } = useSolutionOrg();
  const dashboardEntryRoute = getDashboardEntryRoute(solutionOrgName);
  const computedAfterLoginPath = useMemo(
    () => normalizeLoginRedirectPath(urlAfterLoginPath || afterLoginPath, dashboardEntryRoute),
    [urlAfterLoginPath, afterLoginPath, dashboardEntryRoute],
  );
  const hasExplicitRedirectPath = Boolean(urlAfterLoginPath || afterLoginPath);
  const logger = Logger.create('LoginLayout');
  const [isSsoLogged, setIsSsoLogged] = useState(false);
  const { trackLogin } = useCustomEventTracking();

  const getCompanyEnrollmentInfo = useCompanyEnrollmentInfoForLogin({
    calledFromOnboardingComponent: false,
    hasExplicitRedirectPath,
    redirectPath: computedAfterLoginPath,
  });
  const encodedAfterLoginPath = encodeURIComponent(computedAfterLoginPath);

  useEffect(() => {
    if (isSsoLogged) {
      getCompanyEnrollmentInfo();
    }
  }, [getCompanyEnrollmentInfo, isSsoLogged]);

  // handle passing of sessionId and otp token as query parameters
  useEffect(() => {
    async function autoLogin() {
      if (!initializedQueryLogin.current && urlAccessToken) {
        logger.debug(Logger.format('auto login triggered'));
        initializedQueryLogin.current = true;
        try {
          const loginSessionResponse = await generateTemporaryLoginCodeSso(urlAccessToken);

          if (loginSessionResponse?.redirectHostname) {
            const url = new URL(window.location.href);
            url.protocol = 'https';
            url.port = '';
            url.hostname = loginSessionResponse.redirectHostname;

            if (loginSessionResponse?.redirectParams) {
              Object.entries(loginSessionResponse.redirectParams).forEach(([key, value]) => {
                url.searchParams.set(key, value);
              });
            }

            window.location.href = url.href;
          } else if (loginSessionResponse?.id && loginSessionResponse?.otp) {
            // persist login session id to store
            dispatch(addSessionId(loginSessionResponse.id));
            dispatch(loginMember({
              type: MemberLoginType.OTP,
              password: loginSessionResponse.otp,
              sessionId: loginSessionResponse.id,
            }))
              .then(unwrapResult)
              .then((loginResponse) => {
                setIsSsoLogged(true);
                const loginEventDetail = {
                  memberId: loginResponse.memberId,
                  solutionOrgId: loginResponse.solutionOrgId,
                };
                // Only a magic link requested from /login counts as a login.
                // Integrations, registration and SN forwarding also land here
                // with an access token; track those as auto-logins instead.
                // loginSource comes from the URL and is untrusted (anyone can
                // add it to a link): use it for analytics only, never for
                // auth, routing or access decisions
                if (urlLoginSource === MAGIC_LINK_LOGIN_SOURCE) {
                  trackLogin('Auth', 'OTP', 'Member', {
                    ...loginEventDetail,
                    loginMethod: 'magicLink',
                  });
                } else {
                  trackLogin('Auth', 'AutoLogin', 'Member', {
                    ...loginEventDetail,
                    loginMethod: 'sso',
                    loginSource: toAutoLoginSource(urlLoginSource),
                  });
                }
              })
              .catch((err: unknown) => {
                logger.error(Logger.format(err));
                navigate(`/login?afterLoginPath=${encodedAfterLoginPath}`);
              });
          } else {
            logger.error(Logger.format('failed to get session for auto login'));
            navigate(`/login?afterLoginPath=${encodedAfterLoginPath}`);
          }
        } catch (err: unknown) {
          logger.error(Logger.format(err));
          navigate(`/login?afterLoginPath=${encodedAfterLoginPath}`);
        }
      }
    }

    autoLogin();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppOptimizelyProvider>
      {urlAccessToken ?
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}>
          <CircularProgress color="inherit" size={40} />
        </Box> : <Box
          sx={(theme: Theme) => ({ background: theme.palette.common.white })}
        >
          {
            isDesktop ?
              <LoginDesktopLayout /> :
              <Login />
          }
        </Box>}
    </AppOptimizelyProvider>
  );
}
